import BadgeRounded from '@mui/icons-material/BadgeRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect } from 'react';

import { AppDialog } from '@/components/app-dialog/app-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { hasAdministratorRights } from '@/features/administrator/_shared/domain/is-administrator';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import ClientCategoryField from '../../_shared/ui/client-category-field';
import CountryField from '../../_shared/ui/country-field';
import CsmField from '../../_shared/ui/csm-field';
import { LicenceFieldLabel } from '../../_shared/ui/licence-field';
import { licenceFieldSx } from '../../_shared/ui/licence-field.styles';
import { NAME_TAKEN_MESSAGE } from '../domain/licence-draft.validation';
import {
  clientCategoriesRequested,
  clientCategoryAddRequested,
  csmCandidatesRequested,
  submitRequested,
} from '../store/add-licence.actions';
import {
  selectClientCategories,
  selectCreatedLicenceId,
  selectCsmCandidates,
  selectDraft,
  selectError,
  selectIsDraftValid,
  selectIsNameTaken,
  selectIsSaving,
  selectValidationErrors,
} from '../store/add-licence.selectors';
import { addLicenceActions } from '../store/add-licence.slice';

export interface AddLicenceDialogProps {
  open: boolean;
  onClose: () => void;
  /** New licence id after a successful create; the list uses it to refresh. */
  onCreated?: (id: string) => void;
}

const FIELD_HINT = "Usually the client company's name. You can rename it later.";

const contentSx = { display: 'flex', flexDirection: 'column', gap: 2.5 } as const;

/**
 * Inline "Add Licence" form, in a centred dialog rather than a whole page for
 * four fields (the name, then the client category and country #564 requires,
 * and the Customer Success Manager #583, required since #695 wherever the
 * dialog opens: the Licences page, and Add user creating a licence on the fly). Reuses the whole `add-licence` slice (draft, validation, save saga,
 * gateway); the only thing that differs from the standalone page is the ending:
 * instead of navigating to the index, it hands the new id back and closes in
 * place. The chrome (icon tile, title/subtitle, close, dividers, actions row)
 * comes from the shared `AppDialog` compound.
 *
 * Esc / click-away / Cancel dismiss; Enter submits.
 */
export default function AddLicenceDialog({
  open,
  onClose,
  onCreated,
}: AddLicenceDialogProps) {
  const dispatch = useAppDispatch();
  const { notifyError } = useSnackbar();

  const draft = useAppSelector(selectDraft);
  const isValid = useAppSelector(selectIsDraftValid);
  const isSaving = useAppSelector(selectIsSaving);
  const error = useAppSelector(selectError);
  const validationErrors = useAppSelector(selectValidationErrors);
  const isNameTaken = useAppSelector(selectIsNameTaken);
  const createdLicenceId = useAppSelector(selectCreatedLicenceId);
  const csmCandidates = useAppSelector(selectCsmCandidates);
  const clientCategories = useAppSelector(selectClientCategories);
  // Adding a category needs an Administrator's rights (#604); every staff
  // account since #648; the backend refuses the others.
  const { user } = useAuth();
  const canAddCategory = hasAdministratorRights(user);

  // A fresh draft every time it opens (the slice is a singleton shared with the
  // /add page, so a previous attempt must not leak in) and a fresh list of who
  // may be its CSM (#583) when the field is offered: the reset clears the previous one.
  // The client categories (#604) load every time: the field is always there.
  useEffect(() => {
    if (!open) return;
    dispatch(addLicenceActions.resetForm());
    dispatch(clientCategoriesRequested());
    dispatch(csmCandidatesRequested());
  }, [open, dispatch]);

  // On success: hand the id back (the list refreshes on it), reset, close.
  useEffect(() => {
    if (!createdLicenceId) return;
    onCreated?.(createdLicenceId);
    dispatch(addLicenceActions.resetForm());
    onClose();
  }, [createdLicenceId, onCreated, onClose, dispatch]);

  // A dropped connection is out-of-band, so it stays a snackbar. A rejected name
  // (api) is about this field, so it shows under it (below).
  useEffect(() => {
    if (error?.kind === 'network') notifyError(error.message);
  }, [error, notifyError]);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (isValid && !isSaving && !isNameTaken) dispatch(submitRequested());
    },
    [isValid, isSaving, isNameTaken, dispatch],
  );

  // Local validation, then the live duplicate verdict, then a server rejection
  // (e.g. the 409 duplicate that slipped past the debounce).
  const nameError =
    validationErrors.name ??
    (isNameTaken ? NAME_TAKEN_MESSAGE : undefined) ??
    (error?.kind === 'api' ? error.message : undefined);

  return (
    <AppDialog open={open} onClose={onClose}>
      <Box component="form" onSubmit={handleSubmit}>
        <AppDialog.Header>
          {/* The icon tile gives the modal an identity; the subtitle says what a
              licence is, so the single field needs no explanation of its own. */}
          <AppDialog.Icon>
            <BadgeRounded />
          </AppDialog.Icon>
          <AppDialog.HeaderText>
            <AppDialog.Title>Create a new licence</AppDialog.Title>
            <AppDialog.Subtitle>
              Create a client organisation that will host its own users and lists.
            </AppDialog.Subtitle>
          </AppDialog.HeaderText>
          <AppDialog.CloseButton />
        </AppDialog.Header>

        <AppDialog.Content>
          <Box sx={contentSx}>
          {/* Label sits above the field (not the floating-outlined style): a soft
              filled input, no border, accent focus ring; the recipe shared by the
              three licence fields. */}
          <div>
            <LicenceFieldLabel htmlFor="add-licence-name">Licence name</LicenceFieldLabel>
            <TextField
              id="add-licence-name"
              autoFocus
              fullWidth
              required
              hiddenLabel
              variant="filled"
              placeholder="e.g. Acme Capital"
              value={draft.name}
              onChange={(e) =>
                dispatch(addLicenceActions.fieldChanged({ field: 'name', value: e.target.value }))
              }
              error={Boolean(nameError)}
              helperText={nameError ?? FIELD_HINT}
              disabled={isSaving}
              slotProps={{ input: { disableUnderline: true } }}
              sx={licenceFieldSx}
            />
          </div>

          <ClientCategoryField
            id="add-licence-client-category"
            value={draft.client_category}
            options={clientCategories.items}
            status={clientCategories.status}
            onChange={(value) =>
              dispatch(addLicenceActions.fieldChanged({ field: 'client_category', value }))
            }
            error={validationErrors.client_category}
            disabled={isSaving}
            onAdd={
              canAddCategory ? (label) => dispatch(clientCategoryAddRequested(label)) : undefined
            }
            add={clientCategories.add}
            onAddCleared={() => dispatch(addLicenceActions.clientCategoryAddCleared())}
          />

          <CountryField
            id="add-licence-based-in"
            value={draft.based_in}
            onChange={(value) => dispatch(addLicenceActions.fieldChanged({ field: 'based_in', value }))}
            error={validationErrors.based_in}
            disabled={isSaving}
          />

          <CsmField
            id="add-licence-csm"
            value={draft.csm_user_id}
            options={csmCandidates.items}
            status={csmCandidates.status}
            onChange={(value) =>
              dispatch(addLicenceActions.fieldChanged({ field: 'csm_user_id', value }))
            }
            error={validationErrors.csm_user_id}
            disabled={isSaving}
          />
          </Box>
        </AppDialog.Content>

        <AppDialog.Actions>
          <AppDialog.Cancel />
          <Button
            type="submit"
            variant="soft"
            color="primary"
            // Not disabled during the debounced check; that would flicker on
            // every keystroke; the backend 409 covers a submit that races it.
            disabled={!isValid || isSaving || isNameTaken}
            // No leading icon on a dialog's confirm; a spinner only while saving
            // is a state, not decoration.
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Create licence
          </Button>
        </AppDialog.Actions>
      </Box>
    </AppDialog>
  );
}
